/**
 * Dashboard — visão consolidada do mês selecionado.
 * Tela de entrada impactante com KPIs, gráficos e indicadores de saúde.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft, ArrowUpRight, ArrowDownRight, Minus, TrendingUp, AlertTriangle,
  Wallet, Activity, BarChart3, ShieldAlert, Loader2,
  CheckCircle2, Heart, PiggyBank, Home as HomeIcon,
  Share2, Lightbulb, ChevronDown, Sparkles, Compass,
  Radar, ShieldCheck, Check, X, Pencil, Search, FileText, RefreshCw, FilePlus2, FileCheck2,
} from "lucide-react";
import { VisualizadorPaginas } from "./VisualizadorPaginas";
import { usePermissoes } from "../lib/permissoes";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import { api, resumoUrl, relatorioInconsistenciasUrl } from "../lib/api";
import { GLASS_CHART_CARD, CHART_AXIS, CHART_GRID, CHART_TOOLTIP, PIE_COLORS_DARK } from "../lib/chartTheme";

interface Props {
  competenciaId: number;
  condominioId: number;
  /** Quando "relatorios", rola até o painel de Relatórios Detalhados ao abrir. */
  secao?: string;
  onVoltar: () => void;
  onAbrirRelatorio?: (relatorio: string) => void;
  onAbrirHistorico?: () => void;
  onAbrirAuditoriaFiscal?: () => void;
}

interface KpiCard {
  valor: number;
  delta_abs: number | null;
  delta_pct: number | null;
  melhor_quando: string;
}
interface SaldoConta {
  conta_codigo: string;
  conta_nome: string;
  saldo_anterior: number;
  receitas: number;
  despesas: number;
  saldo_final: number;
}
interface DespCat {
  categoria: string;
  valor: number;
  percentual: number;
}
interface Saude {
  meses_cobertura: number | null;
  fundo_reserva_pct: number | null;
  cotas_em_dia: number;
  cotas_total: number;
  cotas_pct: number;
}
interface Alerta {
  tipo: string;
  severidade: string;
  mensagem: string;
  link_relatorio: string | null;
}
interface DashboardData {
  competencia_id: number;
  periodo_str: string;
  periodo_anterior_str: string | null;
  condominio_id: number;
  condominio_nome: string;
  receita_unc: KpiCard;
  despesa_unc: KpiCard;
  resultado: KpiCard;
  saldo_geral: KpiCard;
  saldos_por_conta: SaldoConta[];
  despesas_por_categoria: DespCat[];
  saude: Saude;
  alertas: Alerta[];
}


function fmtBRL(n: number, compacto = false): string {
  if (compacto && Math.abs(n) >= 1000) {
    return "R$ " + (n / 1000).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "k";
  }
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtDelta(n: number): string {
  return (n >= 0 ? "+" : "") + n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

// =====================================================================
// Componentes
// =====================================================================
function KpiCardComp({
  titulo, kpi, icon, corGradiente, comparativo,
}: {
  titulo: string;
  kpi: KpiCard;
  icon: React.ReactNode;
  corGradiente: string;
  comparativo: string | null;
}) {
  // Decide cor do delta: positivo para receita = bom (verde), negativo para despesa = bom (verde)
  let corDelta = "text-slate-500";
  let IconeDelta = Minus;
  if (kpi.delta_abs != null && kpi.delta_abs !== 0) {
    const positivoBom = kpi.melhor_quando === "alto" ? kpi.delta_abs > 0 : kpi.delta_abs < 0;
    corDelta = positivoBom ? "text-emerald-600" : "text-rose-600";
    IconeDelta = kpi.delta_abs > 0 ? ArrowUpRight : ArrowDownRight;
  }

  return (
    <div className={`relative overflow-hidden rounded-xl ${corGradiente} p-5 shadow-md transition-transform hover:scale-[1.02] hover:shadow-lg`}>
      <div className="absolute -top-4 -right-4 opacity-10 text-white">
        <div style={{ fontSize: "5rem" }}>{icon}</div>
      </div>
      <div className="relative">
        <p className="text-xs font-semibold text-white/80 uppercase tracking-wider">{titulo}</p>
        <p className="mt-2 text-3xl font-bold text-white tabular-nums">
          {fmtBRL(kpi.valor)}
        </p>
        {kpi.delta_abs != null && comparativo && (
          <div className="mt-3 flex items-center gap-1.5 text-xs">
            <div className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-white/20 font-semibold ${corDelta === "text-emerald-600" ? "text-emerald-200" : corDelta === "text-rose-600" ? "text-rose-200" : "text-white/70"}`}>
              <IconeDelta size={11} />
              {fmtDelta(kpi.delta_abs)}
              {kpi.delta_pct != null && (
                <span className="opacity-70">({kpi.delta_pct >= 0 ? "+" : ""}{kpi.delta_pct.toFixed(1)}%)</span>
              )}
            </div>
            <span className="text-white/60">vs {comparativo}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function SaudeCard({ icone, titulo, valor, sub, cor, aviso }: {
  icone: React.ReactNode;
  titulo: string;
  valor: string;
  sub: string;
  cor: "emerald" | "amber" | "rose" | "blue";
  aviso?: string | null;
}) {
  const coresFundo: Record<typeof cor, string> = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    blue: "bg-blue-50 border-blue-200 text-blue-900",
  };
  const coresIcone: Record<typeof cor, string> = {
    emerald: "text-emerald-600",
    amber: "text-amber-600",
    rose: "text-rose-600",
    blue: "text-blue-600",
  };
  return (
    <div className={`rounded-xl border ${coresFundo[cor]} p-4 flex items-start gap-3`}>
      <div className={`${coresIcone[cor]} mt-0.5`}>{icone}</div>
      <div className="flex-1">
        <p className="text-xs uppercase tracking-wider opacity-70 font-semibold">{titulo}</p>
        <p className="text-2xl font-bold tabular-nums">{valor}</p>
        <p className="text-xs opacity-75 mt-0.5">{sub}</p>
        {aviso && (
          <p className="mt-2 flex items-start gap-1 text-[11px] font-medium leading-snug">
            <AlertTriangle size={13} className="shrink-0 mt-px" />
            <span>{aviso}</span>
          </p>
        )}
      </div>
    </div>
  );
}

// Paleta de acentos dos cards (vidro escuro com brilho colorido no topo).
const CARD_ACCENTS: { hex: string; rgb: string }[] = [
  { hex: "#22C9B8", rgb: "34,201,184" },   // teal
  { hex: "#9684F6", rgb: "150,132,246" },  // violeta
  { hex: "#E8A13A", rgb: "232,161,58" },   // âmbar
  { hex: "#5A8CE6", rgb: "90,140,230" },   // azul
  { hex: "#34C484", rgb: "52,196,132" },   // verde
  { hex: "#E87BA4", rgb: "232,123,164" },  // rosa
];

function CardRelatorio({
  icone, titulo, descricao, contador, badge, onClick, accentIndex = 0,
}: {
  icone: React.ReactNode;
  titulo: string;
  descricao: string;
  contador?: string;
  badge?: { texto: string; cor: string };
  onClick?: () => void;
  accentIndex?: number;
}) {
  const acc = CARD_ACCENTS[accentIndex % CARD_ACCENTS.length];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      style={{
        background:
          `radial-gradient(130% 85% at 50% -12%, rgba(${acc.rgb},0.30), transparent 55%),` +
          `linear-gradient(180deg, #17263f 0%, #0f1a2e 100%)`,
        borderColor: "rgba(255,255,255,0.10)",
        boxShadow: "0 14px 34px rgba(8,15,30,0.35), inset 0 1px 0 rgba(255,255,255,0.06)",
      }}
      className="group relative text-left rounded-xl border p-3 transition-all hover:-translate-y-0.5 disabled:opacity-55 disabled:cursor-not-allowed disabled:hover:translate-y-0"
    >
      {/* Borda superior "neon" na cor do acento */}
      <span
        aria-hidden
        className="absolute inset-x-0 top-0 h-[3px] rounded-t-xl transition-all group-hover:h-[4px]"
        style={{
          background: acc.hex,
          boxShadow: `0 0 8px 1px rgba(${acc.rgb},0.9), 0 0 18px 3px rgba(${acc.rgb},0.55)`,
        }}
      />
      {badge && (
        <span className={`absolute top-2 right-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${badge.cor}`}>
          {badge.texto}
        </span>
      )}
      <div className="flex items-center gap-2">
        <span className="shrink-0 group-hover:scale-105 transition-transform" style={{ color: acc.hex }}>
          {icone}
        </span>
        <h4 className="font-semibold text-white text-[13px] leading-tight">{titulo}</h4>
      </div>
      <p className="text-[11px] text-slate-300/75 mt-1 leading-snug">{descricao}</p>
      {contador && (
        <p className="text-[11px] font-semibold mt-1.5" style={{ color: acc.hex }}>{contador}</p>
      )}
    </button>
  );
}

// =====================================================================
// Insight do mês (gerado localmente a partir dos números — sem custo)
// =====================================================================
function montarInsight(data: DashboardData) {
  const s = data.saude;
  const inad = Math.max(0, 100 - s.cotas_pct);
  const resultadoPos = data.resultado.valor >= 0;
  const coberturaOk = s.meses_cobertura != null && s.meses_cobertura >= 3;
  const fundoOk = s.fundo_reserva_pct != null && s.fundo_reserva_pct >= 50;

  let veredito: string;
  if (resultadoPos && coberturaOk && inad < 5) {
    veredito = "Mês saudável: resultado positivo e reserva adequada. Siga acompanhando a inadimplência.";
  } else if (!resultadoPos && !coberturaOk) {
    veredito = "Mês de atenção: resultado negativo e saldo apertado — priorize cobrança e revisão de despesas.";
  } else if (inad >= 8) {
    veredito = "Contas em ordem, mas a inadimplência está alta e pede ação agora.";
  } else if (resultadoPos) {
    veredito = "Mês equilibrado: resultado positivo, com alguns pontos a observar abaixo.";
  } else {
    veredito = "Mês de atenção: o resultado ficou negativo — veja os pontos abaixo.";
  }

  const direcionamento: string[] = [];
  if (s.meses_cobertura != null)
    direcionamento.push(
      `Saldo cobre ${s.meses_cobertura.toFixed(1)} ${s.meses_cobertura === 1 ? "mês" : "meses"} de despesa` +
      (coberturaOk ? " (acima do mínimo de 3)." : " (abaixo do ideal de 3)."),
    );
  if (s.fundo_reserva_pct != null)
    direcionamento.push(
      `Fundo de reserva em ${s.fundo_reserva_pct.toFixed(0)}% do recomendado` + (fundoOk ? "." : " — ainda abaixo de 50%."),
    );
  direcionamento.push(
    `Resultado do mês: ${fmtBRL(data.resultado.valor)}` +
    (data.resultado.delta_pct != null ? ` (${data.resultado.delta_pct >= 0 ? "+" : ""}${data.resultado.delta_pct.toFixed(0)}% vs mês anterior).` : "."),
  );

  const preocupacoes: string[] = [];
  if (inad > 0)
    preocupacoes.push(`${inad.toFixed(0)}% das cotas não quitadas (${s.cotas_total - s.cotas_em_dia} de ${s.cotas_total} unidades).`);
  for (const a of data.alertas) {
    if (a.tipo === "cobertura" || a.tipo === "fundo_reserva") continue;
    if (a.severidade === "red" || a.severidade === "yellow") preocupacoes.push(a.mensagem);
  }
  if (preocupacoes.length === 0) preocupacoes.push("Nenhum ponto crítico identificado neste mês.");

  const sugestoes: string[] = [];
  if (inad >= 3) sugestoes.push("Acompanhe as unidades em atraso no relatório de Cotas e use a 2ª via de boleto no Portal.");
  if (!fundoOk) sugestoes.push("Considere reforçar o fundo de reserva (chamada extra por fração ideal).");
  if (!coberturaOk) sugestoes.push("Reveja as maiores despesas em Despesas por Categoria antes de novos gastos.");
  if (data.alertas.some(a => a.tipo === "fiscal")) sugestoes.push("Confira os Documentos Fiscais e gere o relatório para a administradora.");
  sugestoes.push("Publique a prestação de contas no Portal do Condômino — transparência reduz questionamentos.");

  return {
    veredito,
    direcionamento,
    preocupacoes,
    sugestoes,
    radar: "Em breve: alerta preditivo de inadimplência e comparativo com condomínios semelhantes.",
  };
}

function InsightBloco({ cor, titulo, itens }: { cor: string; titulo: string; itens: string[] }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.04] p-3" style={{ borderTopColor: cor, borderTopWidth: 2 }}>
      <div className="text-[11px] font-bold uppercase tracking-wide mb-1.5" style={{ color: cor }}>{titulo}</div>
      <ul className="space-y-1">
        {itens.map((t, i) => (
          <li key={i} className="text-[12px] text-slate-200/90 leading-snug flex gap-1.5">
            <span style={{ color: cor }} className="shrink-0">•</span><span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface InsightIA {
  veredito: string;
  direcionamento: string[];
  preocupacoes: string[];
  sugestoes: string[];
  radar: string;
  fonte?: string;
  aviso?: string | null;
}

function InsightCard({ data, competenciaId, podeGerarIA, onAbrirRelatorio }: {
  data: DashboardData;
  competenciaId: number;
  podeGerarIA: boolean;
  onAbrirRelatorio?: (relatorio: string) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const heur = useMemo(() => montarInsight(data), [data]);
  const [ia, setIa] = useState<InsightIA | null>(null);
  const [carregandoIA, setCarregandoIA] = useState(false);
  const [erroIA, setErroIA] = useState<string | null>(null);
  const insight = ia ?? heur;
  const RGB = "34,201,184", HEX = "#22C9B8";

  async function gerarIA() {
    if (carregandoIA) return;
    const ok = window.confirm(
      "Gerar o insight aprofundado com IA?\n\nEsta ação usa a inteligência artificial e " +
      "tem custo (entra no Uso & Custos do condomínio). O insight grátis continua disponível.",
    );
    if (!ok) return;
    setCarregandoIA(true);
    setErroIA(null);
    try {
      const r = await api.post<InsightIA>(`/competencias/${competenciaId}/insight-ia`);
      if (r.fonte === "ia") {
        setIa(r);
        setAberto(true);
      } else {
        setErroIA(r.aviso || "Não foi possível gerar o insight com IA agora.");
      }
    } catch {
      setErroIA("Falha ao falar com a IA. Tente novamente.");
    } finally {
      setCarregandoIA(false);
    }
  }
  return (
    <div className="mb-6">
      <div
        style={{
          background:
            `radial-gradient(120% 160% at 0% 0%, rgba(${RGB},0.20), transparent 46%),` +
            `linear-gradient(180deg,#17263f 0%,#0f1a2e 100%)`,
          borderColor: "rgba(255,255,255,0.10)",
          boxShadow: "0 14px 34px rgba(8,15,30,0.35), inset 0 1px 0 rgba(255,255,255,0.06)",
        }}
        className="relative rounded-xl border"
      >
        <span
          aria-hidden
          className="absolute inset-x-0 top-0 h-[3px] rounded-t-xl"
          style={{ background: HEX, boxShadow: `0 0 8px 1px rgba(${RGB},0.9), 0 0 18px 3px rgba(${RGB},0.55)` }}
        />
        <button
          type="button"
          onClick={() => setAberto(v => !v)}
          className="w-full text-left px-4 py-3 flex items-center gap-3"
        >
          <span className="shrink-0 grid place-items-center w-8 h-8 rounded-lg" style={{ color: "#0E8D82", background: HEX }}>
            <Lightbulb size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-white font-semibold text-[13px]">Insights · {data.condominio_nome}</span>
              <span className="text-[10px] font-semibold text-teal-900 bg-teal-300 rounded-full px-1.5 py-0.5">{data.periodo_str}</span>
            </div>
            <p className={`text-slate-300/85 text-[12px] leading-snug mt-0.5 ${aberto ? "" : "truncate"}`}>{insight.veredito}</p>
          </div>
          <ChevronDown size={18} className={`shrink-0 text-slate-300 transition-transform ${aberto ? "rotate-180" : ""}`} />
        </button>

        {aberto && (
          <div className="px-4 pb-4 pt-1">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <InsightBloco cor="#34C484" titulo="Direcionamento" itens={insight.direcionamento} />
              <InsightBloco cor="#E8A13A" titulo="Preocupações" itens={insight.preocupacoes} />
              <InsightBloco cor="#5A8CE6" titulo="Sugestões" itens={insight.sugestoes} />
            </div>
            <div className="mt-3 flex items-start gap-2 text-[12px] text-slate-300/85 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
              <Compass size={14} className="text-indigo-300 shrink-0 mt-0.5" />
              <span><b className="text-white">No radar:</b> {insight.radar}</span>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {podeGerarIA && !ia && (
                <button
                  type="button"
                  onClick={gerarIA}
                  disabled={carregandoIA}
                  className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-white rounded-lg px-3 py-1.5 disabled:opacity-60"
                  style={{ background: "linear-gradient(135deg,#1fd3c1,#0E8D82)" }}
                >
                  {carregandoIA ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                  {carregandoIA ? "Gerando com IA…" : "Aprofundar com IA (Com Custo)"}
                </button>
              )}
              {ia && (
                <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-teal-200 bg-teal-500/15 border border-teal-400/30 rounded-lg px-3 py-1.5">
                  <Sparkles size={13} /> Aprofundado com IA
                </span>
              )}
              {onAbrirRelatorio && (
                <button
                  type="button"
                  onClick={() => onAbrirRelatorio("tendencia")}
                  className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-white bg-white/10 hover:bg-white/15 border border-white/10 rounded-lg px-3 py-1.5"
                >
                  <TrendingUp size={13} /> Ver tendência
                </button>
              )}
            </div>
            {erroIA && (
              <p className="mt-2 text-[11px] text-amber-300 flex items-center gap-1.5">
                <AlertTriangle size={12} /> {erroIA}
              </p>
            )}
            <p className="mt-2 text-[10px] text-slate-400/80 leading-snug">
              {ia
                ? "Análise aprofundada gerada por IA (com custo, registrada em Uso & Custos)."
                : "Insight grátis, gerado a partir dos números do mês."}
              {" "}Apoio à decisão — não substitui o conselho nem a assembleia.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// =====================================================================
// Dashboard Page
// =====================================================================
// =====================================================================
// Pontos de Atenção (auditor de padrões) — grátis + justificativa
// =====================================================================
interface PontoAtencao {
  chave: string; tipo: string; conta: string; categoria: string; titulo: string;
  severidade: string; valor: number; referencia: number | null; motivo: string;
  sugestao: string; pagina?: string | null;
  paginas?: { rotulo: string; pagina: string }[] | null;
  justificativa: string | null; justificado_por: string | null;
  justificado_em: string | null;
  no_relatorio?: boolean;   // 0.1.80: vai para o Relatório de Inconsistências
}
interface PontosResp {
  competencia_id: number; itens: PontoAtencao[]; total: number; abertos: number;
  por_severidade: Record<string, number>;
  analise_ia?: AnalisePadroesIA | null;
  analise_em?: string | null;
  analise_por?: string | null;
}
interface AnalisePadroesIA {
  veredito: string; leitura: string; prioridades: string[];
  reclassificacoes: string[]; perguntas: string[];
  fonte: string; aviso: string | null;
}

const SEV: Record<string, { rotulo: string; hex: string; rgb: string }> = {
  alta: { rotulo: "Atenção alta", hex: "#f43f5e", rgb: "244,63,94" },
  media: { rotulo: "Verificar", hex: "#f59e0b", rgb: "245,158,11" },
  info: { rotulo: "Informativo", hex: "#38bdf8", rgb: "56,189,248" },
};

// =====================================================================
// Documentos por linha (procedência persistida) — 0.1.68
// Correlaciona cada linha do balancete com os documentos que a compõem
// (Balancete, Resumo de DOC's, NFs, DARFs/Guias e a FATURA da concessionária
// localizada no OCR). Carrega sob demanda (só quando o usuário abre o painel),
// pois a 1ª correlação varre o OCR; depois é instantâneo (persistido no banco).
// =====================================================================
type DocLinha = {
  tipo: string; rotulo?: string | null; pagina?: string | null;
  numero?: string | null; fornecedor?: string | null; valor?: number | null;
  metodo: string; confianca?: number | null;
};
type LinhaComDocs = {
  linha_chave: string; rotulo?: string | null; conta?: string | null;
  valor?: number | null; status?: string; documentos: DocLinha[];
};
type LinhaDocsResp = {
  competencia_id: number; linhas: LinhaComDocs[];
  total_linhas: number; total_documentos: number;
  precisa_correlacionar?: boolean;
  desatualizada?: boolean;
};

const DOC_LABEL: Record<string, string> = {
  BALANCETE: "Linha no balancete", RESUMO_DOCS: "Resumo de DOC's",
  NF: "Nota Fiscal", BOLETO: "Boleto", RECIBO: "Recibo", EMAIL: "E-mail",
  DARF: "DARF", GUIA_ISS: "Guia de ISS", CONCESSIONARIA: "Fatura",
  COMPROVANTE: "Comprovante", DOC: "Documento",
};

function DocumentosPorLinhaCard({ competenciaId, podeGerir }: {
  competenciaId: number; podeGerir: boolean;
}) {
  const qc = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [paginaViewer, setPaginaViewer] = useState<number | null>(null);
  const pagNum = (p?: string | null): number | null => {
    const m = /(\d+)/.exec(p || "");
    return m ? Number(m[1]) : null;
  };
  const { data, isLoading, isFetching } = useQuery<LinhaDocsResp>({
    queryKey: ["linha-documentos", competenciaId],
    queryFn: () => api.get(`/competencias/${competenciaId}/linha-documentos`),
    enabled: aberto,           // só busca (e correlaciona) quando o usuário abre
    staleTime: 5 * 60 * 1000,
  });
  const recorrelacionar = useMutation({
    mutationFn: () => api.post(`/competencias/${competenciaId}/correlacionar-documentos`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["linha-documentos", competenciaId] }),
  });

  const todas = data?.linhas ?? [];
  const linhas = todas.filter((l) => (l.status ?? "ok") === "ok" && l.documentos.length > 0);
  const semDoc = todas.filter((l) => l.status === "sem_doc");
  const orfaos = todas.filter((l) => l.status === "orfaos");

  return (
    <div className="mb-6 rounded-2xl border p-4 overflow-hidden relative" style={GLASS_CHART_CARD}>
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        className="w-full flex items-center justify-between gap-2"
      >
        <h3 className="text-sm font-semibold text-slate-100 flex items-center gap-1.5">
          <FileText size={16} className="text-sky-300" />
          Documentos por linha <span className="text-slate-400 font-normal">· procedência</span>
        </h3>
        <div className="flex items-center gap-2">
          {data && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/10 text-slate-300">
              {data.total_documentos} docs · {linhas.length} linhas
            </span>
          )}
          <ChevronDown size={16} className={`text-slate-400 transition-transform ${aberto ? "rotate-180" : ""}`} />
        </div>
      </button>

      {aberto && (
        <div className="mt-3">
          {isLoading || isFetching ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-6 justify-center">
              <Loader2 size={16} className="animate-spin" /> Lendo correlações…
            </div>
          ) : recorrelacionar.isPending ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-6 justify-center">
              <Loader2 size={16} className="animate-spin" /> Correlacionando documentos da pasta… (pode levar alguns segundos)
            </div>
          ) : data?.precisa_correlacionar ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <p className="text-sm text-slate-400 max-w-md">
                {data.desatualizada
                  ? "A correlação deste mês foi feita pela versão anterior, que às vezes apontava a página errada. Clique abaixo para refazer com a versão nova (carimbo da administradora, DARF e guia de ISS)."
                  : "Ainda não correlacionei os documentos deste mês. Clique abaixo para varrer a pasta uma vez e amarrar cada linha do histórico às suas NFs, boletos e recibos."}
              </p>
              {podeGerir ? (
                <button
                  type="button"
                  onClick={() => recorrelacionar.mutate()}
                  className="inline-flex items-center gap-2 text-sm px-3.5 py-2 rounded-lg border border-teal-400/40 text-teal-200 bg-teal-500/10 hover:bg-teal-500/20"
                >
                  <RefreshCw size={15} /> Correlacionar documentos
                </button>
              ) : (
                <span className="text-xs text-slate-500">Peça a um gestor para correlacionar os documentos.</span>
              )}
            </div>
          ) : linhas.length === 0 && semDoc.length === 0 && orfaos.length === 0 ? (
            <div className="text-slate-400 text-sm py-6 text-center">
              Nenhum documento correlacionado para este mês.
            </div>
          ) : (
            <>
              {podeGerir && (
                <div className="flex justify-end mb-2">
                  <button
                    type="button"
                    onClick={() => recorrelacionar.mutate()}
                    disabled={recorrelacionar.isPending}
                    title="Recalcular a correlação (após reprocessar o mês ou adicionar documentos à pasta)"
                    className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md border border-white/15 text-slate-300 hover:bg-white/10 disabled:opacity-50"
                  >
                    {recorrelacionar.isPending ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                    Atualizar
                  </button>
                </div>
              )}
              <div className="space-y-2">
                {linhas.map((l) => (
                  <div key={l.linha_chave} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      {l.conta && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-slate-300">{l.conta}</span>
                      )}
                      <span className="text-sm font-semibold text-slate-100">{l.rotulo}</span>
                      {l.valor != null && (
                        <span className="text-xs text-slate-400">
                          {l.valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {l.documentos.map((d, i) => {
                        const n = pagNum(d.pagina);
                        const label = d.rotulo || DOC_LABEL[d.tipo] || d.tipo;
                        return n != null ? (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setPaginaViewer(n)}
                            title={`Abrir a página ${n}${d.metodo === "ocr_scan" ? " (localizada no OCR)" : ""}`}
                            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border border-white/15 text-slate-300 hover:bg-white/10"
                          >
                            <Search size={12} /> {label} · pág. {n}
                          </button>
                        ) : (
                          <span
                            key={i}
                            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border border-white/10 text-slate-400"
                          >
                            <FileText size={12} /> {label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Não correlacionados — para o Airton ir melhorando as descrições */}
              {(semDoc.length > 0 || orfaos.length > 0) && (
                <div className="mt-4 pt-3 border-t border-white/10">
                  <div className="flex items-center gap-1.5 text-[12px] font-semibold text-amber-300/90 mb-2">
                    <AlertTriangle size={13} /> Ainda não correlacionados
                    <span className="text-slate-500 font-normal">· ajude a melhorar</span>
                  </div>
                  {semDoc.length > 0 && (
                    <div className="mb-2">
                      <div className="text-[11px] text-slate-400 mb-1">
                        Linhas do histórico sem documento encontrado ({semDoc.length}):
                      </div>
                      <div className="space-y-1">
                        {semDoc.map((l) => (
                          <div key={l.linha_chave} className="flex items-center gap-2 text-[12px] text-slate-300">
                            {l.conta && <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10">{l.conta}</span>}
                            <span>{l.rotulo}</span>
                            {l.valor != null && (
                              <span className="text-slate-500">
                                {l.valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {orfaos.length > 0 && (
                    <div>
                      <div className="text-[11px] text-slate-400 mb-1">
                        Documentos na pasta sem linha no histórico ({orfaos.reduce((a, l) => a + l.documentos.length, 0)}):
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {orfaos.flatMap((l) => l.documentos).map((d, i) => {
                          const n = pagNum(d.pagina);
                          const label = d.rotulo || DOC_LABEL[d.tipo] || d.tipo;
                          return n != null ? (
                            <button
                              key={i}
                              type="button"
                              onClick={() => setPaginaViewer(n)}
                              title={`Abrir a página ${n}`}
                              className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border border-amber-400/25 text-amber-200/90 hover:bg-amber-500/10"
                            >
                              <Search size={12} /> {label} · pág. {n}
                            </button>
                          ) : (
                            <span key={i} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border border-white/10 text-slate-400">
                              <FileText size={12} /> {label}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <p className="text-[11px] text-slate-500 mt-3">
                Cada linha aponta a NF/boleto/recibo na pasta (a lupa abre a página exata). As faturas
                de concessionária são localizadas pelo texto já digitalizado — sem custo.
              </p>
            </>
          )}
        </div>
      )}

      {paginaViewer != null && (
        <VisualizadorPaginas
          competenciaId={competenciaId}
          paginas={[paginaViewer]}
          aberto={paginaViewer != null}
          onFechar={() => setPaginaViewer(null)}
        />
      )}
    </div>
  );
}


export function PontosAtencaoCard({ competenciaId, podeJustificar }: {
  competenciaId: number; podeJustificar: boolean;
}) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<PontosResp>({
    queryKey: ["pontos-atencao", competenciaId],
    queryFn: () => api.get(`/competencias/${competenciaId}/pontos-atencao`),
  });
  const [editando, setEditando] = useState<string | null>(null);
  const [texto, setTexto] = useState("");
  const [paginaViewer, setPaginaViewer] = useState<number | null>(null);
  const pagNum = (p?: string | null): number | null => {
    const m = /(\d+)/.exec(p || "");
    return m ? Number(m[1]) : null;
  };
  const inval = () => qc.invalidateQueries({ queryKey: ["pontos-atencao", competenciaId] });
  const salvar = useMutation({
    mutationFn: ({ chave, texto }: { chave: string; texto: string }) =>
      api.post(`/competencias/${competenciaId}/pontos-atencao/justificar`, { chave, texto }),
    onSuccess: () => { setEditando(null); setTexto(""); inval(); },
  });
  const remover = useMutation({
    mutationFn: (chave: string) =>
      api.delete(`/competencias/${competenciaId}/pontos-atencao/justificar?chave=${encodeURIComponent(chave)}`),
    onSuccess: inval,
  });
  // 0.1.80: liga/desliga o ponto no Relatório de Inconsistências (para a administradora).
  const [erroRel, setErroRel] = useState<string | null>(null);
  const noRelatorio = useMutation({
    mutationFn: ({ chave, incluir }: { chave: string; incluir: boolean }) =>
      api.post(`/competencias/${competenciaId}/pontos-atencao/relatorio`, { chave, incluir }),
    onMutate: () => setErroRel(null),
    onSuccess: inval,
    onError: (e) => setErroRel((e as Error).message || "Não foi possível alterar agora."),
  });

  // Painel recolhível (0.1.74). Desde a 0.1.88 começa SEMPRE recolhido ao entrar
  // (pedido do Airton: aberto, poluía o painel); o selo "N em aberto" continua à vista
  // e abrir é um clique. Não guarda mais a escolha no navegador.
  const [recolhido, setRecolhido] = useState<boolean>(true);
  const alternarRecolhido = () => setRecolhido((v) => !v);

  const [analise, setAnalise] = useState<AnalisePadroesIA | null>(null);
  const [analiseAberta, setAnaliseAberta] = useState(false);  // relatório IA recolhível
  const analiseIA = useMutation({
    mutationFn: () => api.post<AnalisePadroesIA>(`/competencias/${competenciaId}/analise-ia`),
    onSuccess: (r) => { setAnalise(r); setAnaliseAberta(true); },  // abre ao gerar
  });
  const pedirAnalise = () => {
    if (window.confirm("A análise por IA tem custo (entra no Uso & Custos). Deseja continuar?")) {
      analiseIA.mutate();
    }
  };

  const itens = data?.itens ?? [];
  // Mostra a análise recém-gerada (local) OU a última salva no cache (sem custo).
  const analiseView = analise ?? data?.analise_ia ?? null;
  const analiseEmCache = !analise && !!data?.analise_ia;
  const analiseData = data?.analise_em
    ? new Date(data.analise_em).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" })
    : null;

  return (
    <div className="mb-6 rounded-2xl border p-4 overflow-hidden relative" style={GLASS_CHART_CARD}>
      <div className={`flex items-center justify-between gap-2 ${recolhido ? "" : "mb-3"}`}>
        <button type="button" onClick={alternarRecolhido}
          title={recolhido ? "Abrir os Pontos de Atenção" : "Recolher os Pontos de Atenção"}
          className="text-sm font-semibold text-slate-100 flex items-center gap-1.5 text-left">
          <Radar size={16} className="text-teal-300" />
          Pontos de Atenção <span className="text-slate-400 font-normal">· auditor de padrões</span>
        </button>
        <div className="flex items-center gap-2">
          {data && data.total > 0 && (
            <span className="text-[11px] px-2 py-0.5 rounded-full"
              style={{ background: data.abertos > 0 ? "rgba(244,63,94,0.18)" : "rgba(52,211,153,0.18)",
                       color: data.abertos > 0 ? "#fca5a5" : "#6ee7b7" }}>
              {data.abertos > 0 ? `${data.abertos} em aberto` : "tudo justificado"}
            </span>
          )}
          {podeJustificar && itens.length > 0 && (
            <button onClick={pedirAnalise} disabled={analiseIA.isPending} title="Análise holística por IA (tem custo)"
              className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md border border-violet-400/40 text-violet-200 bg-violet-500/10 hover:bg-violet-500/20 disabled:opacity-50">
              {analiseIA.isPending ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
              {analiseView ? "Atualizar análise" : "Analisar com IA"} <span className="text-violet-300/80">(Com Custo)</span>
            </button>
          )}
          <button type="button" onClick={alternarRecolhido}
            title={recolhido ? "Abrir" : "Recolher"} aria-expanded={!recolhido}
            className="p-1 rounded-md hover:bg-white/10">
            <ChevronDown size={16} className={`text-slate-400 transition-transform ${recolhido ? "" : "rotate-180"}`} />
          </button>
        </div>
      </div>

      {!recolhido && (<>
      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-6 justify-center">
          <Loader2 size={16} className="animate-spin" /> Analisando o histórico…
        </div>
      ) : itens.length === 0 ? (
        <div className="flex items-center gap-2 text-emerald-300/90 text-sm py-6 justify-center">
          <ShieldCheck size={18} /> Nada fora do padrão neste mês. As despesas seguem o histórico.
        </div>
      ) : (
        <div className="space-y-2.5">
          {erroRel && <div className="text-xs text-rose-300">{erroRel}</div>}
          {podeJustificar && itens.some((i) => i.no_relatorio) && (
            <div className="text-[11px] text-violet-200/90">
              {itens.filter((i) => i.no_relatorio).length} ponto(s) marcado(s) vão no
              {" "}<b>Relatório p/ administradora</b> (botão no topo do painel), com as páginas do PDF.
            </div>
          )}
          {itens.map((p) => {
            const sev = SEV[p.severidade] ?? SEV.info;
            const just = !!p.justificativa;
            return (
              <div key={p.chave} className="rounded-xl border p-3"
                style={{ borderColor: `rgba(${sev.rgb},0.35)`,
                         background: `linear-gradient(180deg, rgba(${sev.rgb},0.08), rgba(255,255,255,0.02))` }}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-semibold"
                        style={{ background: `rgba(${sev.rgb},0.18)`, color: sev.hex }}>{sev.rotulo}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-slate-300">{p.conta}</span>
                      <span className="text-sm font-semibold text-slate-100">{p.titulo}</span>
                    </div>
                    <div className="text-sm text-slate-200 mt-1">{p.motivo}</div>
                    <div className="text-xs text-slate-400 mt-1">💡 {p.sugestao}</div>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {(p.paginas && p.paginas.length > 0
                        ? p.paginas
                        : (p.pagina ? [{ rotulo: "", pagina: p.pagina }] : [])
                      ).map((pg, i) => (
                        pagNum(pg.pagina) != null && (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setPaginaViewer(pagNum(pg.pagina))}
                            title={`Ver a página ${pagNum(pg.pagina)} do balancete`}
                            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border border-white/15 text-slate-300 hover:bg-white/10"
                          >
                            <Search size={12} /> {pg.rotulo ? `${pg.rotulo} · ` : ""}pág. {pagNum(pg.pagina)}
                          </button>
                        )
                      ))}
                    </div>
                  </div>
                  <div className="flex-shrink-0 flex flex-col items-end gap-1">
                    {podeJustificar ? (
                      <button type="button"
                        onClick={() => noRelatorio.mutate({ chave: p.chave, incluir: !p.no_relatorio })}
                        disabled={noRelatorio.isPending && noRelatorio.variables?.chave === p.chave}
                        aria-pressed={!!p.no_relatorio}
                        title={p.no_relatorio
                          ? "Este ponto vai no Relatório de Inconsistências da administradora. Clique para tirar."
                          : "Incluir este ponto no Relatório de Inconsistências enviado à administradora"}
                        className={"inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border disabled:opacity-50 " +
                          (p.no_relatorio
                            ? "border-violet-300/60 bg-violet-500/25 text-violet-100"
                            : "border-white/15 text-slate-300 hover:bg-white/10")}>
                        {noRelatorio.isPending && noRelatorio.variables?.chave === p.chave
                          ? <Loader2 size={12} className="animate-spin" />
                          : p.no_relatorio ? <FileCheck2 size={12} /> : <FilePlus2 size={12} />}
                        {p.no_relatorio ? "No relatório da adm." : "Levar ao relatório"}
                      </button>
                    ) : p.no_relatorio ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-violet-200">
                        <FileCheck2 size={12} /> no relatório da adm.
                      </span>
                    ) : null}
                    {just && (
                      <span title="Justificado" className="inline-flex items-center gap-1 text-[11px] text-emerald-300">
                        <ShieldCheck size={14} /> justificado
                      </span>
                    )}
                  </div>
                </div>

                {/* Justificativa */}
                {just && editando !== p.chave ? (
                  <div className="mt-2 rounded-lg bg-white/[0.04] border border-white/10 p-2.5">
                    <div className="text-xs text-slate-200">“{p.justificativa}”</div>
                    <div className="text-[10px] text-slate-500 mt-1">
                      {p.justificado_por ? `por ${p.justificado_por}` : ""}
                    </div>
                    {podeJustificar && (
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => { setEditando(p.chave); setTexto(p.justificativa || ""); }}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-white/15 text-slate-200 hover:bg-white/10"><Pencil size={12} /> Editar</button>
                        <button onClick={() => { if (confirm("Remover a justificativa?")) remover.mutate(p.chave); }}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-rose-400/30 text-rose-300 hover:bg-rose-500/10"><X size={12} /> Remover</button>
                      </div>
                    )}
                  </div>
                ) : podeJustificar && editando === p.chave ? (
                  <div className="mt-2">
                    <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={2}
                      placeholder="Explique o motivo (ex.: obra do deck aprovada na AGE de 03/2026)…"
                      className="w-full text-sm rounded-md bg-white/[0.06] border border-white/15 text-slate-100 px-2.5 py-2 placeholder:text-slate-500" />
                    <div className="flex gap-2 mt-1.5">
                      <button onClick={() => texto.trim() && salvar.mutate({ chave: p.chave, texto: texto.trim() })}
                        disabled={salvar.isPending || !texto.trim()}
                        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50">
                        {salvar.isPending ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Salvar
                      </button>
                      <button onClick={() => { setEditando(null); setTexto(""); }}
                        className="text-xs px-3 py-1.5 rounded-md border border-white/15 text-slate-300">Cancelar</button>
                    </div>
                  </div>
                ) : podeJustificar ? (
                  <button onClick={() => { setEditando(p.chave); setTexto(""); }}
                    className="mt-2 inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md border border-white/15 text-slate-200 hover:bg-white/10">
                    <Pencil size={12} /> Justificar
                  </button>
                ) : null}
              </div>
            );
          })}
          {/* Análise holística por IA (com custo) — recém-gerada ou salva no cache */}
          {analiseView && (
            <div className="rounded-xl border p-3 mt-1"
              style={{ borderColor: "rgba(167,139,250,0.35)",
                       background: "linear-gradient(180deg, rgba(139,92,246,0.12), rgba(255,255,255,0.02))" }}>
              <button type="button" onClick={() => setAnaliseAberta((v) => !v)}
                className="w-full flex items-center justify-between gap-2 text-left"
                title={analiseAberta ? "Recolher relatório da IA" : "Abrir relatório da IA"}>
                <div className="flex items-center gap-1.5 text-violet-200 text-xs font-semibold">
                  <Sparkles size={14} /> Análise holística (IA)
                  {!analiseAberta && <span className="text-slate-400 font-normal">· clique para abrir</span>}
                </div>
                <div className="flex items-center gap-2">
                  {analiseEmCache && analiseData && (
                    <span className="text-[10px] text-slate-400">última análise · {analiseData}</span>
                  )}
                  <ChevronDown size={16}
                    className={`text-violet-200 transition-transform ${analiseAberta ? "rotate-180" : ""}`} />
                </div>
              </button>
              {analiseAberta && (analiseView.aviso && analiseView.fonte !== "ia" ? (
                <div className="text-sm text-amber-200/90">{analiseView.aviso}</div>
              ) : (
                <div className="space-y-2">
                  {analiseView.veredito && <div className="text-sm font-medium text-slate-100">{analiseView.veredito}</div>}
                  {analiseView.leitura && <p className="text-sm text-slate-200 leading-relaxed">{analiseView.leitura}</p>}
                  {analiseView.prioridades.length > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-violet-300/90 mb-0.5">Prioridades</div>
                      <ul className="list-disc pl-5 text-sm text-slate-200 space-y-0.5">
                        {analiseView.prioridades.map((x, i) => <li key={i}>{x}</li>)}
                      </ul>
                    </div>
                  )}
                  {analiseView.reclassificacoes.length > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-violet-300/90 mb-0.5">Reclassificações sugeridas</div>
                      <ul className="list-disc pl-5 text-sm text-slate-200 space-y-0.5">
                        {analiseView.reclassificacoes.map((x, i) => <li key={i}>{x}</li>)}
                      </ul>
                    </div>
                  )}
                  {analiseView.perguntas.length > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-violet-300/90 mb-0.5">Perguntas para justificar</div>
                      <ul className="list-disc pl-5 text-sm text-slate-200 space-y-0.5">
                        {analiseView.perguntas.map((x, i) => <li key={i}>{x}</li>)}
                      </ul>
                    </div>
                  )}
                  <div className="text-[10px] text-slate-500">Gerado por IA a partir dos números e dos pontos de atenção. Revise antes de decidir.</div>
                </div>
              ))}
            </div>
          )}
          {analiseIA.isError && (
            <div className="text-sm text-rose-300 mt-1">Não foi possível gerar a análise agora.</div>
          )}

          <p className="text-[11px] text-slate-500 pt-1">
            Análise automática (custo zero) do padrão histórico. Justifique o que for esperado —
            fica registrado para a prestação de contas.
          </p>
        </div>
      )}
      </>)}

      {paginaViewer != null && (
        <VisualizadorPaginas
          competenciaId={competenciaId}
          paginas={[paginaViewer]}
          aberto={paginaViewer != null}
          onFechar={() => setPaginaViewer(null)}
        />
      )}
    </div>
  );
}

export function DashboardPage({
  competenciaId, condominioId, secao, onVoltar, onAbrirRelatorio, onAbrirHistorico,
  onAbrirAuditoriaFiscal,
}: Props) {
  // "Resumo (WhatsApp)" e "Relatório p/ administradora" são ações de gestão externa:
  // só síndico/Dono. Os relatórios detalhados seguem a leitura do balancete.
  const { ehSindico } = usePermissoes();
  const podeGerir = ehSindico(condominioId);
  const { data, isLoading, error } = useQuery<DashboardData>({
    queryKey: ["dashboard", competenciaId],
    queryFn: () => api.get<DashboardData>(`/competencias/${competenciaId}/dashboard`),
  });

  // Ao voltar de um relatório, rola direto até a fileira de botões de
  // "Relatórios Detalhados" — assim o usuário escolhe o próximo sem procurar.
  useEffect(() => {
    if (!data || secao !== "relatorios") return;
    const t = window.setTimeout(() => {
      document
        .getElementById("painel-relatorios")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => window.clearTimeout(t);
  }, [data, secao]);

  const [resumoMsg, setResumoMsg] = useState<string | null>(null);

  // Opção B: abre o arquivo em NOVA ABA (o navegador mostra o visualizador com
  // opções de baixar/imprimir).
  const abrirResumo = (formato: "pdf" | "png") => {
    setResumoMsg(
      "Abrindo o resumo numa nova aba — use os botões de baixar/imprimir do navegador " +
      "para salvar e enviar no WhatsApp. Se não abrir, libere os pop-ups deste site.",
    );
    window.open(resumoUrl(competenciaId, formato), "_blank");
  };
  const abrirRelatorio = () => {
    setResumoMsg(
      "Abrindo o relatório de inconsistências numa nova aba — baixe ou imprima para " +
      "enviar à administradora. Se não abrir, libere os pop-ups deste site.",
    );
    window.open(relatorioInconsistenciasUrl(competenciaId), "_blank");
  };

  const dadosGraficoSaldo = useMemo(() => {
    if (!data) return [];
    return data.saldos_por_conta.map(s => ({
      conta: s.conta_nome,
      Anterior: s.saldo_anterior,
      Final: s.saldo_final,
    }));
  }, [data]);

  const dadosPizza = useMemo(() => {
    if (!data) return [];
    return data.despesas_por_categoria.slice(0, 12).map(d => ({
      name: d.categoria,
      value: d.valor,
    }));
  }, [data]);

  // Avisos de saúde financeira saem do topo e vão para dentro dos cards.
  const alertasInline = (data?.alertas ?? []).filter(
    (a) => a.tipo !== "cobertura" && a.tipo !== "fundo_reserva",
  );
  const avisoCobertura = (data?.alertas ?? []).find((a) => a.tipo === "cobertura")?.mensagem ?? null;
  const avisoFundoReserva = (data?.alertas ?? []).find((a) => a.tipo === "fundo_reserva")?.mensagem ?? null;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-slate-50 flex items-center justify-center">
        <div className="text-slate-500 flex items-center gap-2">
          <Loader2 size={18} className="animate-spin" />
          Carregando dashboard...
        </div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <button onClick={onVoltar} className="text-slate-500 hover:text-slate-900 text-sm inline-flex items-center gap-1">
          <ArrowLeft size={16} /> Voltar
        </button>
        <div className="mt-4 p-6 bg-rose-50 border border-rose-200 rounded-xl text-rose-800">
          Erro ao carregar dashboard.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/20 to-slate-50">
      <div className="max-w-7xl mx-auto px-4 py-6">

        {/* Topo: navegação + botões */}
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <button
            onClick={onVoltar}
            className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft size={16} /> Voltar
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Histórico e Auditoria Fiscal foram para o painel "Relatórios Detalhados"
                (abaixo). Aqui ficam só as ações de gestão externa (síndico/Dono). */}
            {podeGerir && (
              <>
                <button
                  type="button"
                  onClick={() => abrirResumo("pdf")}
                  title="Abre o Resumo do Mês em PDF numa nova aba — baixe ou imprima para enviar no WhatsApp"
                  className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-md shadow-sm"
                >
                  <Share2 size={14} />
                  Resumo (WhatsApp)
                </button>
                <button
                  type="button"
                  onClick={abrirRelatorio}
                  title="Abre um PDF com as inconsistências do mês (impostos, duplicidade, divergências e os Pontos de Atenção marcados) numa nova aba, para baixar/imprimir e enviar à administradora"
                  className="inline-flex items-center gap-2 px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded-md shadow-sm"
                >
                  <AlertTriangle size={14} />
                  Relatório p/ administradora
                </button>
              </>
            )}
          </div>
        </div>

        {resumoMsg && (
          <div className="mb-4 flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
            <span>{resumoMsg}</span>
            <button type="button" onClick={() => setResumoMsg(null)} className="text-emerald-700 hover:text-emerald-900 text-xs">fechar</button>
          </div>
        )}

        {/* Hero header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-900">{data.condominio_nome}</h1>
          <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5">
            <Activity size={14} />
            Dashboard · <span className="font-semibold text-indigo-700">{data.periodo_str}</span>
            {data.periodo_anterior_str && (
              <span className="text-slate-400">· comparativo: {data.periodo_anterior_str}</span>
            )}
          </p>
        </div>

        {/* Alertas inline — exceto os de saúde financeira (cobertura/fundo de
            reserva), que agora aparecem DENTRO dos cards de Saúde Financeira. */}
        {alertasInline.length > 0 && (
          <div className="mb-5 space-y-2">
            {alertasInline.map((al, i) => {
              const cores: Record<string, string> = {
                red:    "bg-rose-50 border-rose-300 text-rose-900",
                yellow: "bg-amber-50 border-amber-300 text-amber-900",
                blue:   "bg-blue-50 border-blue-300 text-blue-900",
              };
              return (
                <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${cores[al.severidade] || cores.blue}`}>
                  <AlertTriangle size={16} className="flex-shrink-0" />
                  <span className="text-sm flex-1">{al.mensagem}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiCardComp
            titulo="RECEITA UNC"
            kpi={data.receita_unc}
            icon={<TrendingUp />}
            corGradiente="bg-gradient-to-br from-emerald-500 to-emerald-700"
            comparativo={data.periodo_anterior_str}
          />
          <KpiCardComp
            titulo="DESPESA UNC"
            kpi={data.despesa_unc}
            icon={<Wallet />}
            corGradiente="bg-gradient-to-br from-rose-500 to-rose-700"
            comparativo={data.periodo_anterior_str}
          />
          <KpiCardComp
            titulo="RESULTADO"
            kpi={data.resultado}
            icon={<Activity />}
            corGradiente="bg-gradient-to-br from-indigo-500 to-indigo-700"
            comparativo={data.periodo_anterior_str}
          />
          <KpiCardComp
            titulo="SALDO GERAL"
            kpi={data.saldo_geral}
            icon={<PiggyBank />}
            corGradiente="bg-gradient-to-br from-slate-700 to-slate-900"
            comparativo={data.periodo_anterior_str}
          />
        </div>

        {/* Gráficos: Saldos por Conta + Despesas por Categoria */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* Saldos por Conta */}
          <div className="rounded-2xl border p-5 overflow-hidden relative" style={GLASS_CHART_CARD}>
            <h3 className="text-sm font-semibold text-slate-100 mb-3 flex items-center gap-1.5">
              <BarChart3 size={16} className="text-indigo-300" />
              Saldos por Conta
            </h3>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={dadosGraficoSaldo} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <defs>
                    <linearGradient id="barAnterior" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#a78bfa" stopOpacity={0.95} />
                      <stop offset="100%" stopColor="#a78bfa" stopOpacity={0.45} />
                    </linearGradient>
                    <linearGradient id="barFinal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#38bdf8" stopOpacity={1} />
                      <stop offset="100%" stopColor="#38bdf8" stopOpacity={0.5} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 6" stroke={CHART_GRID} vertical={false} />
                  <XAxis dataKey="conta" tick={{ fontSize: 11, fill: CHART_AXIS }} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.10)" }} />
                  <YAxis tickFormatter={(v) => fmtBRL(v, true)} tick={{ fontSize: 10, fill: CHART_AXIS }} tickLine={false} axisLine={false} />
                  <Tooltip formatter={(v: number) => fmtBRL(v)} cursor={{ fill: "rgba(255,255,255,0.05)" }}
                    contentStyle={CHART_TOOLTIP} labelStyle={{ color: "#e2e8f0" }} itemStyle={{ color: "#e2e8f0" }} />
                  <Legend wrapperStyle={{ fontSize: "11px", color: "#cbd5e1" }} />
                  <Bar dataKey="Anterior" fill="url(#barAnterior)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Final" fill="url(#barFinal)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Despesas por Categoria */}
          <div className="rounded-2xl border p-5 overflow-hidden relative" style={GLASS_CHART_CARD}>
            <h3 className="text-sm font-semibold text-slate-100 mb-3 flex items-center gap-1.5">
              <Wallet size={16} className="text-rose-300" />
              Despesas por Categoria — Conta Principal (UNC)
            </h3>
            {dadosPizza.length === 0 ? (
              <div className="text-xs text-slate-400 text-center py-12">Sem despesas categorizadas neste mês.</div>
            ) : (
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={dadosPizza}
                      cx="50%" cy="50%"
                      innerRadius={50} outerRadius={95}
                      paddingAngle={2}
                      dataKey="value"
                      stroke="#0c1122" strokeWidth={2}
                    >
                      {dadosPizza.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS_DARK[i % PIE_COLORS_DARK.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: number) => fmtBRL(v)}
                      contentStyle={CHART_TOOLTIP} labelStyle={{ color: "#e2e8f0" }} itemStyle={{ color: "#e2e8f0" }} />
                    <Legend
                      verticalAlign="middle" align="right" layout="vertical"
                      wrapperStyle={{ fontSize: "10px", maxWidth: "45%", lineHeight: 1.4, color: "#cbd5e1" }}
                      formatter={(v) => <span style={{ color: "#cbd5e1" }}>{v.toString().slice(0, 22)}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* Saúde Financeira */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-1.5">
            <Heart size={16} className="text-rose-500" />
            Saúde Financeira
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <SaudeCard
              icone={<TrendingUp size={28} />}
              titulo="Meses de Cobertura"
              valor={data.saude.meses_cobertura != null ? data.saude.meses_cobertura.toFixed(1) : "—"}
              sub="Saldo / despesa mensal"
              cor={data.saude.meses_cobertura != null && data.saude.meses_cobertura >= 3 ? "emerald" : "amber"}
              aviso={avisoCobertura}
            />
            <SaudeCard
              icone={<PiggyBank size={28} />}
              titulo="Fundo de Reserva"
              valor={data.saude.fundo_reserva_pct != null ? `${data.saude.fundo_reserva_pct.toFixed(0)}%` : "—"}
              sub="% da despesa anual estimada"
              cor={data.saude.fundo_reserva_pct != null && data.saude.fundo_reserva_pct >= 50 ? "emerald" : "amber"}
              aviso={avisoFundoReserva}
            />
            <SaudeCard
              icone={<HomeIcon size={28} />}
              titulo="Cotas pagas"
              valor={`${data.saude.cotas_em_dia}/${data.saude.cotas_total}`}
              sub={`${data.saude.cotas_pct.toFixed(0)}% quitadas no mês`}
              cor={data.saude.cotas_pct >= 95 ? "emerald" : data.saude.cotas_pct >= 80 ? "amber" : "rose"}
            />
          </div>
        </div>

        {/* Insights (card fino, entre Saúde Financeira e Relatórios Detalhados) */}
        <InsightCard
          data={data}
          competenciaId={competenciaId}
          podeGerarIA={podeGerir}
          onAbrirRelatorio={onAbrirRelatorio}
        />

        {/* Pontos de Atenção (auditor de padrões) */}
        <PontosAtencaoCard competenciaId={competenciaId} podeJustificar={podeGerir} />

        {/* Documentos por linha (procedência persistida) */}
        <DocumentosPorLinhaCard competenciaId={competenciaId} podeGerir={podeGerir} />

        {/* Painel de Relatórios */}
        <div id="painel-relatorios" className="scroll-mt-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-1.5">
            <CheckCircle2 size={16} className="text-indigo-600" />
            Relatórios Detalhados
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
            <CardRelatorio
              accentIndex={0}
              icone={<TrendingUp size={20} strokeWidth={2} />}
              titulo="Tendência (12 meses)"
              descricao="Receita, despesa, resultado e saldo — UNC e FRE"
              onClick={onAbrirRelatorio ? () => onAbrirRelatorio("tendencia") : undefined}
            />
            <CardRelatorio
              accentIndex={1}
              icone={<Wallet size={20} strokeWidth={2} />}
              titulo="Resumo de Saldos"
              descricao="Por conta contábil"
              contador={`${data.saldos_por_conta.length} contas`}
              onClick={onAbrirRelatorio ? () => onAbrirRelatorio("saldos") : undefined}
            />
            <CardRelatorio
              accentIndex={2}
              icone={<BarChart3 size={20} strokeWidth={2} />}
              titulo="Despesas por Categoria (UNC e FRE)"
              descricao="Conta Principal (UNC) + Fundo de Reserva (FRE)"
              contador={`${data.despesas_por_categoria.length} categorias`}
              onClick={onAbrirRelatorio ? () => onAbrirRelatorio("despesas") : undefined}
            />
            <CardRelatorio
              accentIndex={3}
              icone={<HomeIcon size={20} strokeWidth={2} />}
              titulo="Cotas Condominiais"
              descricao="Status por unidade"
              contador={`${data.saude.cotas_em_dia}/${data.saude.cotas_total} em dia`}
              onClick={onAbrirRelatorio ? () => onAbrirRelatorio("cotas") : undefined}
            />
            <CardRelatorio
              accentIndex={4}
              icone={<PiggyBank size={20} strokeWidth={2} />}
              titulo="Chamadas Extras"
              descricao="Fundo de Reserva por unidade"
              onClick={onAbrirRelatorio ? () => onAbrirRelatorio("chamadas-extras") : undefined}
            />
            <CardRelatorio
              accentIndex={5}
              icone={<ShieldAlert size={20} strokeWidth={2} />}
              titulo="Documentos Fiscais"
              descricao="NFs · DARFs · Guias"
              badge={data.alertas.some(a => a.tipo === "fiscal") ? { texto: "🚩 alerta", cor: "bg-rose-500/20 text-rose-200" } : undefined}
              onClick={onAbrirRelatorio ? () => onAbrirRelatorio("fiscal") : undefined}
            />
            <CardRelatorio
              accentIndex={0}
              icone={<CheckCircle2 size={20} strokeWidth={2} />}
              titulo="Auditoria M1"
              descricao="Validações automáticas"
              onClick={onAbrirRelatorio ? () => onAbrirRelatorio("findings") : undefined}
            />
            <CardRelatorio
              accentIndex={1}
              icone={<Activity size={20} strokeWidth={2} />}
              titulo="Resumo de DOC's"
              descricao="Documentos do mês"
              onClick={onAbrirRelatorio ? () => onAbrirRelatorio("docs") : undefined}
            />
            {onAbrirHistorico && (
              <CardRelatorio
                accentIndex={2}
                icone={<BarChart3 size={20} strokeWidth={2} />}
                titulo="Histórico de Fornecedores"
                descricao="Gastos por fornecedor ao longo dos meses"
                onClick={onAbrirHistorico}
              />
            )}
            {onAbrirAuditoriaFiscal && (
              <CardRelatorio
                accentIndex={3}
                icone={<ShieldAlert size={20} strokeWidth={2} />}
                titulo="Auditoria Fiscal"
                descricao="Retenções e pagamentos (6 meses)"
                onClick={onAbrirAuditoriaFiscal}
              />
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
